import { autocomplete } from '@algolia/autocomplete-js';
import { createLocalStorageRecentSearchesPlugin } from '@algolia/autocomplete-plugin-recent-searches';
import {
  createTypesenseSuggestionsPlugin,
  SuggestionItem,
} from './createTypesenseSuggestionsPlugin';
import { scheduleDefaultsPrefetch } from './defaults_prefetch';
import { normalizeForCompare } from './utils/queryNormalization';

scheduleDefaultsPrefetch();

const MOBILE_DEBOUNCE_MS: number = 150;
const DESKTOP_DEBOUNCE_MS: number = 150;
const MOBILE_STALL_THRESHOLD_MS: number = MOBILE_DEBOUNCE_MS + 100;
const DESKTOP_STALL_THRESHOLD_MS: number = DESKTOP_DEBOUNCE_MS + 300;
const RECENT_SEARCHES_LIMIT = 3;
const RECENT_SEARCHES_PLUGIN_KEY = 'typesense_recent_searches';
// The plugin internally prefixes the key with 'AUTOCOMPLETE_RECENT_SEARCHES:'
const RECENT_SEARCHES_STORAGE_KEY = `AUTOCOMPLETE_RECENT_SEARCHES:${RECENT_SEARCHES_PLUGIN_KEY}`;
// Guards against repeated ce.commoninit passes on the same DOM node.
const initializedContainers = new WeakSet<HTMLElement>();

// Module-level device detection — stable for the lifetime of the page.
const isMobileDevice =
  window.matchMedia('(pointer: coarse)').matches ||
  window.matchMedia('(max-width: 767px)').matches;

type RecentSearchItem = {
  id: string;
  label: string;
  hash_id?: string;
  query?: string;
  normalized?: string;
  _highlightResult: {
    label: {
      value: string;
    };
  };
};

type AnalyticsSelectPayload = {
  item?: any;
  source?: any;
  state?: any;
  event?: Event;
};

type LazyAnalyticsPlugin = {
  name: string;
  subscribe: (params: { onSelect: (handler: (payload: AnalyticsSelectPayload) => void) => void }) => void;
  preload: () => Promise<void>;
};

function escapeRegExp(value: string) {
  return String(value || '').replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');
}

function withHighlight(item: RecentSearchItem, query: string): RecentSearchItem {
  const label = String(item?.label || '');
  const highlighted = query
    ? label.replace(new RegExp(escapeRegExp(query), 'gi'), (match) => `__aa-highlight__${match}__/aa-highlight__`)
    : label;

  return {
    ...item,
    _highlightResult: {
      label: {
        value: highlighted,
      },
    },
  };
}

function buildSearchUrl(baseUrl: string, query: string) {
  try {
    const url = new URL(baseUrl, window.location.origin);
    url.searchParams.set('q', query || '');
    return url.toString();
  } catch (_error) {
    return `${baseUrl}?q=${encodeURIComponent(query || '')}`;
  }
}

function saveRecentSearch(label: string, hashId?: string) {
  const value = String(label || '').trim();
  const hash = String(hashId || '').trim();
  if (!value) {
    return;
  }

  try {
    const raw = window.localStorage.getItem(RECENT_SEARCHES_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    const items = Array.isArray(parsed) ? parsed : [];
    const next = [
      {
        id: value,
        label: value,
        hash_id: hash || undefined,
        normalized: normalizeForCompare(value),
      },
      ...items.filter((item: any) => String(item?.id || item?.label || '') !== value),
    ].slice(0, RECENT_SEARCHES_LIMIT);

    window.localStorage.setItem(RECENT_SEARCHES_STORAGE_KEY, JSON.stringify(next));
  } catch (_error) {
  }
}

function createLazyTypesenseAnalyticsPlugin(): LazyAnalyticsPlugin {
  let delegate: ((payload: AnalyticsSelectPayload) => void) | null = null;
  let queue: AnalyticsSelectPayload[] = [];
  let loadingPromise: Promise<void> | null = null;

  const flushQueue = () => {
    if (!delegate || !queue.length) {
      return;
    }

    const pending = queue;
    queue = [];

    pending.forEach((payload) => {
      try {
        delegate?.(payload);
      } catch (_error) {
      }
    });
  };

  const preload = () => {
    if (delegate) {
      return Promise.resolve();
    }

    if (loadingPromise) {
      return loadingPromise;
    }

    loadingPromise = import('./createTypesenseAnalyticsPlugin')
      .then((module) => {
        const plugin = module.createTypesenseAnalyticsPlugin();
        plugin.subscribe?.({
          onSelect(handler: (payload: AnalyticsSelectPayload) => void) {
            delegate = handler;
          },
        } as any);

        flushQueue();
      })
      .catch(() => {
        queue = [];
      })
      .finally(() => {
        loadingPromise = null;
      });

    return loadingPromise;
  };

  return {
    name: 'typesenseLazyAnalyticsPlugin',
    subscribe({ onSelect }) {
      onSelect((payload) => {
        if (delegate) {
          delegate(payload);
          return;
        }

        queue.push(payload);
        void preload();
      });
    },
    preload,
  };
}

function initAutocomplete(container: HTMLElement) {
  if (initializedContainers.has(container)) {
    return;
  }

  const stallThreshold = isMobileDevice
    ? MOBILE_STALL_THRESHOLD_MS
    : DESKTOP_STALL_THRESHOLD_MS;

  const target = container.querySelector('.typesense-autocomplete') as HTMLElement | null;
  if (!target) {
    return;
  }

  const endpoint = container.getAttribute('data-ca-typesense-endpoint') || '';
  const defaultsEndpoint =
    container.getAttribute('data-ca-typesense-defaults-endpoint') || '';
  const searchResultsUrl =
    container.getAttribute('data-ca-typesense-search-url') || '';
  const perPage = isMobileDevice ? 6 : 10;
  const placeholder =
    container.getAttribute('data-ca-typesense-placeholder') || '';
  const fillQueryTitle =
    container.getAttribute('data-ca-typesense-fill-query-title') || '';
  const defaultsLang =
    container.getAttribute('data-ca-typesense-defaults-lang') || 'en';
  const defaultsTtl = Math.max(
    parseInt(container.getAttribute('data-ca-typesense-defaults-ttl') || '3600', 10) || 3600,
    0
  );
  const initialQuery =
    container.getAttribute('data-ca-typesense-initial-query') || '';

  if (!endpoint && !defaultsEndpoint) {
    return;
  }

  const skeleton = target.querySelector('.tsa-skeleton');
  if (skeleton) {
    skeleton.remove();
  }

  initializedContainers.add(container);

  const recentSearchesPlugin = createLocalStorageRecentSearchesPlugin({
    key: RECENT_SEARCHES_PLUGIN_KEY,
    limit: RECENT_SEARCHES_LIMIT,
    transformSource({ source }) {
      return {
        ...source,
        getItemUrl(params: any) {
          const label = String(params?.item?.label || '').trim();
          return label ? buildSearchUrl(searchResultsUrl, label) : undefined;
        },
        onSelect(params: any) {
          const label = String(params?.item?.label || '').trim();
          if (!label) {
            return;
          }

          window.location.assign(buildSearchUrl(searchResultsUrl, label));
        },
      };
    },
    subscribe({ onSelect }) {
      onSelect(({ item, source }) => {
        if (source.sourceId !== 'querySuggestionsPlugin') {
          return;
        }

        const label = String((item as any)?.label || '').trim();
        const hashId = String((item as any)?.hash_id || (item as any)?.id || '').trim();
        if (!label) {
          return;
        }

        saveRecentSearch(label, hashId);
      });
    },
    search({ query, items, limit }) {
      const normalizedQuery = normalizeForCompare(query);
      const plainQuery = String(query || '').trim().toLowerCase();
      const preparedItems = items as RecentSearchItem[];

      return preparedItems
        .filter((item) => {
          if (!normalizedQuery) {
            return true;
          }

          const label = String(item?.label || '').toLowerCase();
          const normalized = String(item?.normalized || '');

          return label.includes(plainQuery) || normalized.includes(normalizedQuery);
        })
        .slice(0, limit)
        .map((item) => withHighlight(item, query));
    },
  });

  const typesenseSuggestionsPlugin = createTypesenseSuggestionsPlugin({
    endpoint,
    defaultsEndpoint,
    searchResultsUrl,
    perPage,
    fillQueryTitle,
    defaultsLang,
    defaultsTtl,
    isMobileDevice,
    initialQuery,
    mobileDebounceMs: MOBILE_DEBOUNCE_MS,
    desktopDebounceMs: DESKTOP_DEBOUNCE_MS,
    getRecentItems(query) {
      return (recentSearchesPlugin.data?.getAll(query) ?? []) as RecentSearchItem[];
    },
  });

  const typesenseAnalyticsPlugin = createLazyTypesenseAnalyticsPlugin();

  const warmUpAnalytics = () => {
    void typesenseAnalyticsPlugin.preload();
    target.removeEventListener('focusin', warmUpAnalytics, true);
    target.removeEventListener('input', warmUpAnalytics, true);
  };

  target.addEventListener('focusin', warmUpAnalytics, true);
  target.addEventListener('input', warmUpAnalytics, true);

  autocomplete<SuggestionItem>({
    container: target,
    placeholder,
    initialState: initialQuery ? { query: initialQuery } : undefined,
    openOnFocus: true,
    detachedMediaQuery: 'none',
    stallThreshold,
    plugins: [recentSearchesPlugin, typesenseSuggestionsPlugin, typesenseAnalyticsPlugin],
    onSubmit({ state, event }) {
      const submitted = state.query.trim();
      if (!submitted) {
        return;
      }

      saveRecentSearch(submitted);
      event?.preventDefault?.();
      window.location.assign(buildSearchUrl(searchResultsUrl, submitted));
    },
  });

  // Sync initial query to DOM after Preact render
  if (initialQuery) {
    requestAnimationFrame(() => {
      const input = target.querySelector('.aa-Input') as HTMLInputElement | null;
      if (input && input.value !== initialQuery) {
        input.value = initialQuery;
      }
    });
  }

}

// Single module-level outside-click handler — no per-instance listeners on document.
function handleOutsidePointerDown(event: Event) {
  const element = event.target instanceof Element ? event.target : null;
  if (!element) return;

  // Check if click is inside any autocomplete UI
  if (
    element.closest('[data-ca-typesense-autocomplete]')
    || element.closest('.aa-Panel')
    || element.closest('.aa-Item')
    || element.closest('.aa-Form')
  ) {
    return;
  }

  // Close the panel by blurring — do NOT dispatch Escape, which resets the query.
  document.querySelectorAll<HTMLInputElement>('.aa-Input').forEach((input) => {
    input.blur();
  });
}

document.addEventListener('pointerdown', handleOutsidePointerDown, true);

function mountAll() {
  document.querySelectorAll<HTMLElement>('[data-ca-typesense-autocomplete]').forEach((el) => {
    initAutocomplete(el);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', mountAll, { once: true });
} else {
  mountAll();
}
